/**
 * Vincent 2026-06-01 — Source de vérité UNIQUE pour les rôles affichés/sélectionnés
 * partout dans la plateforme.
 *
 * Vincent #408 (Eric 2026-07-04 SPEC STRICK — XLSX consignes-FLHQ) — Ordre canonique
 * confirmé DE Nouveau : du MOINS équipé au PLUS équipé en terme d'accès
 * (comme présenté dans "Sélection des accès").
 *
 *   Visiteur · Nouvel employé · Employé · Employé formé · Employé avancé ·
 *   Concepteur · Admin · Fondateur
 *
 * (`nouveau` est un alias legacy de `employee_new` — placé après pour rétro-compat.)
 */

// Ordre canonique — du MOINS privilégié au PLUS privilégié (Vincent XLSX 2026-07-04).
export const CANONICAL_ROLE_IDS = [
  "viewer",
  "employee_new",
  "employee",
  "employee_trained",
  "employee_advanced",
  "concepteur",
  "admin",
  "founder",
  "nouveau", // legacy — traité comme employee_new mais gardé en queue
];

// Tâches 1386 · 1387 (Eric 2026-06-30) — Les rôles restaient français en
// version anglaise. Point non évident : `user.role_labels` contient les noms
// que l'organisation a elle-même saisis (page « Libellés des rôles ») —
// « Conseiller actif », « Concepteur de formations », « Admin technique » n'ont
// jamais été des libellés d'interface, ce sont des DONNÉES. On ne les traduit
// donc pas : les traduire reviendrait à inventer le vocabulaire du client.
// Seuls les libellés PAR DÉFAUT, ceux que personne n'a renommés, prennent leur
// version anglaise.
const DEFAULT_LABELS_EN = {
  viewer: "Viewer",
  employee_new: "New employee",
  employee: "Employee",
  employee_trained: "Trained employee",
  employee_advanced: "Advanced employee",
  concepteur: "Designer",
  admin: "Admin",
  founder: "Founder",
  nouveau: "New",
};

/** Libellé par défaut d'un rôle dans la langue demandée. */
export function defaultRoleLabel(id, lang) {
  return (lang === "en" ? DEFAULT_LABELS_EN[id] : DEFAULT_LABELS[id]) || DEFAULT_LABELS[id];
}

// Libellés par défaut si role_labels manque (e.g. utilisateur non authentifié).
const DEFAULT_LABELS = {
  viewer: "Visiteur",
  employee_new: "Nouvel employé",
  employee: "Employé",
  employee_trained: "Employé formé",
  employee_advanced: "Employé avancé",
  concepteur: "Concepteur",
  admin: "Admin",
  founder: "Fondateur",
  nouveau: "Nouveau",
};

// Mapping des IDs legacy (president/ceo/vp) vers le canonique correspondant.
// Vincent 2026-06-01 — Ces 3 rôles ne font plus partie de la liste canonique :
// ils sont tous absorbés par "Admin" (super-pouvoirs).
const LEGACY_TO_CANONICAL = {
  president: "admin",
  ceo: "admin",
  vp: "admin",
};

/**
 * Retourne la liste des rôles canoniques [{id, label}] dans l'ordre Vincent,
 * en utilisant les libellés personnalisés de l'org si disponibles.
 * @param {object|null} user — l'objet user retourné par /auth/me (peut contenir .role_labels)
 * @returns {Array<{id: string, label: string}>}
 */
export function getCanonicalRoles(user, lang) {
  const labels = roleLabelsFor(user, lang);
  return CANONICAL_ROLE_IDS.map((id) => ({
    id,
    // Tâches 1386 · 1387 — Un libellé saisi par l'organisation gagne toujours ;
    // sinon on rend le défaut dans la langue en cours.
    label: (labels[id] && String(labels[id]).trim()) || defaultRoleLabel(id, lang),
  }));
}

/**
 * Résout un ID de rôle (legacy ou canonique) en son libellé canonique d'affichage.
 * @param {string} roleId — l'ID stocké (peut être president/ceo/vp/admin/etc.)
 * @param {object|null} roleLabels — la map user.role_labels (optionnel)
 * @param {boolean} isFounder — true si l'utilisateur est marqué `is_founder` (priorité absolue)
 * @returns {string}
 */
/**
 * Tâche 1387 (Eric 2026-06-30) → RENVERSÉE par la tâche 1416 (Eric
 * 2026-09-30) — Jeu de libellés à utiliser selon la langue.
 * `/auth/me` envoie les DEUX (`role_labels` et `role_labels_en`) parce que le
 * serveur ignore dans quelle langue l'écran est regardé : le bouton FR|EN est
 * un réglage du navigateur, pas du compte.
 *
 * 1387 laissait le nom FRANÇAIS saisi par l'organisation s'afficher sur
 * l'écran anglais quand la colonne anglaise était vide. Eric a tranché en
 * 1416 : sur un écran anglais, on n'affiche JAMAIS le libellé français d'une
 * organisation — on retombe sur le libellé anglais par défaut (« Designer »,
 * « Advanced employee »…). La page « Gestion des rôles » reste l'endroit qui
 * signale le trou à combler.
 */
export function roleLabelsFor(user, lang) {
  if (!user) return {};
  if (lang === "en") return user.role_labels_en || {};
  return user.role_labels || {};
}

// Tâche 1415 (Eric 2026-09-30) — Le rôle restait en français sur l'écran
// anglais. La cause : `roleLabelsFor` renvoyait bien `role_labels_en`, mais
// quand l'organisation n'a PAS rempli la colonne anglaise, le repli tombait
// sur la table française. La langue est donc passée jusqu'au repli.
export function getCanonicalLabel(roleId, roleLabels, isFounder = false, lang = "fr") {
  if (isFounder) {
    return (roleLabels && roleLabels.founder) || defaultRoleLabel("founder", lang);
  }
  const canonicalId = LEGACY_TO_CANONICAL[roleId] || roleId;
  if (CANONICAL_ROLE_IDS.includes(canonicalId)) {
    return (roleLabels && roleLabels[canonicalId]) || defaultRoleLabel(canonicalId, lang) || canonicalId;
  }
  return roleId || "";
}

/**
 * Convertit un ID legacy (president/ceo/vp) vers son équivalent canonique.
 * Utile quand on doit pré-sélectionner une option dans un dropdown canonique.
 * @param {string} roleId
 * @returns {string}
 */
export function mapLegacyToCanonical(roleId) {
  return LEGACY_TO_CANONICAL[roleId] || roleId;
}

// Tâche 1402 (Eric 2026-09-30) — « Par souci de cohérence, remplace la clé
// technique de Designer par `designer`. »
//
// La clé RÉELLE reste `concepteur` : c'est la valeur stockée dans `org_role`
// de chaque utilisateur, et elle sert de clé dans la matrice d'accès, les vues
// « Voir comme », les permissions du menu et les courriels. La renommer en
// base demanderait une migration — Eric a choisi de ne changer que la clé
// EXPOSÉE. Cette table est donc la frontière : d'un côté le vocabulaire
// montré, de l'autre celui qui fait autorité.
export const CLES_TECHNIQUES_AFFICHEES = {
  concepteur: "designer",
};

/** La clé technique telle qu'on la MONTRE (voir la note ci-dessus). */
export function cleTechniqueAffichee(role) {
  return CLES_TECHNIQUES_AFFICHEES[role] || role;
}
